import {
  getMemberImportReconciliation,
  getMembers,
  resolveMemberImportException,
  type MemberImportReconciliationRow,
  type MemberImportResolutionAction,
  type MemberListItemContract,
} from '@urbanik/api-client';
import { EmptyState, SearchSelectField, StatePanel, canonicalStates } from '@urbanik/ui';
import { CheckCircle2, ChevronRight, Search, UsersRound } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';
import './member-import-diagnostic.css';
import './reconciliation-workspace.css';
type Board = Awaited<ReturnType<typeof getMemberImportReconciliation>>;
const isVacant = (row: MemberImportReconciliationRow) =>
  row.sourceStatus === 'vacant' || row.fullName === 'CUPO LIBRE';
const isReleased = (row: MemberImportReconciliationRow) =>
  row.resolutionAction === 'confirm-vacant' ||
  row.resolutionAction === 'release-undocumented-holder';
const statusLabel = (action: MemberImportResolutionAction | null) =>
  action
    ? ({
        'confirm-vacant': 'Vacante confirmado',
        'preserve-historical': 'Referencia conservada',
        'create-inactive-member': 'Histórico inactivo',
        'create-active-member': 'Socio activo',
        'create-active-undocumented-member': 'Activo · documentos pendientes',
        'release-undocumented-holder': 'Cupo liberado',
        'assign-existing-member': 'Titular asignado',
      }[action] ?? 'Resuelto')
    : 'Pendiente';

function MemberPicker({
  rowId,
  value,
  disabled,
  onChange,
  onAssign,
}: {
  rowId: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onAssign: () => void;
}) {
  const [query, setQuery] = useState(''),
    [members, setMembers] = useState<readonly MemberListItemContract[]>([]),
    [loading, setLoading] = useState(false);
  useEffect(() => {
    const controller = new AbortController(),
      timer = setTimeout(() => {
        setLoading(true);
        void getMembers(
          apiBase,
          { query, status: 'active', sort: 'name', direction: 'asc', page: 1, pageSize: 20 },
          controller.signal,
        )
          .then((r) => setMembers(r.items))
          .catch(() => setMembers([]))
          .finally(() => setLoading(false));
      }, 180);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);
  return (
    <div className="reconciliation-member-picker">
      <SearchSelectField
        id={`member-${rowId}`}
        label="Reasignar cupo a un socio existente"
        hint="Busca por nombre, cédula o cupo actual."
        placeholder="Buscar socio activo"
        emptyMessage="No se encontraron socios."
        loading={loading}
        value={value}
        onQueryChange={setQuery}
        onValueChange={onChange}
        options={members.map((m) => ({
          value: m.id,
          label: `${m.familyNames} ${m.givenNames}`,
          meta: `${m.nationalId ? `Cédula ${m.nationalId}` : 'Documentación pendiente'}${m.slotNumber ? ` · Cupo ${m.slotNumber}` : ''}`,
        }))}
      />
      <div className="reconciliation-member-picker__actions">
        <a
          className="ui-button ui-button--secondary"
          href="#socios/nuevo?return=conciliacion-socios"
        >
          Crear socio nuevo
        </a>
        <button
          type="button"
          className="ui-button ui-button--primary"
          disabled={disabled || !value}
          onClick={onAssign}
        >
          Reasignar cupo
        </button>
      </div>
    </div>
  );
}

export function MemberImportReconciliationAliquotView() {
  const [state, setState] = useState<'loading' | 'success' | 'error' | 'forbidden'>('loading'),
    [board, setBoard] = useState<Board | null>(null);
  const [filter, setFilter] = useState<'pending' | 'resolved'>('pending'),
    [query, setQuery] = useState(''),
    [selectedId, setSelectedId] = useState('');
  const [reasons, setReasons] = useState<Record<string, string>>({}),
    [nationalIds, setNationalIds] = useState<Record<string, string>>({}),
    [selectedMembers, setSelectedMembers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null),
    [notice, setNotice] = useState<string | null>(null);
  const load = () => {
    setState('loading');
    void getMemberImportReconciliation(apiBase)
      .then((data) => {
        setBoard(data);
        setSelectedId(
          (current) =>
            current || data.rows.find((r) => r.status === 'pending')?.id || data.rows[0]?.id || '',
        );
        setState('success');
      })
      .catch((error) =>
        setState(
          error instanceof Error && error.message.includes('permiso') ? 'forbidden' : 'error',
        ),
      );
  };
  useEffect(load, []);
  const rows = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('es');
    return (
      board?.rows.filter(
        (row) =>
          row.status === filter &&
          (!normalized ||
            `${row.slotNumber ?? ''} ${row.fullName ?? ''} ${row.nationalId ?? ''}`
              .toLocaleLowerCase('es')
              .includes(normalized)),
      ) ?? []
    );
  }, [board, filter, query]);
  useEffect(() => {
    if (rows.length && !rows.some((row) => row.id === selectedId)) setSelectedId(rows[0]!.id);
  }, [rows, selectedId]);
  const selected = rows.find((row) => row.id === selectedId) ?? null;
  const resolve = (
    row: MemberImportReconciliationRow,
    action: MemberImportResolutionAction,
    nationalId?: string,
    existingMemberId?: string,
  ) => {
    const reason = reasons[row.id]?.trim() ?? '';
    if (reason.length < 10) return;
    setBusy(row.id);
    setNotice(null);
    void resolveMemberImportException(apiBase, row.id, action, reason, nationalId, existingMemberId)
      .then(() => {
        setNotice('Decisión guardada con historial y auditoría.');
        load();
      })
      .catch((error) => setNotice(error instanceof Error ? error.message : 'No se pudo guardar.'))
      .finally(() => setBusy(null));
  };
  if (state !== 'success' || !board) {
    const panel = canonicalStates.find((item) => item.kind === state)!;
    return state === 'error' ? (
      <StatePanel state={panel} onAction={load} />
    ) : (
      <StatePanel state={panel} />
    );
  }
  if (!board.rows.length)
    return (
      <EmptyState
        title="Aún no hay socios para conciliar"
        description="Primero descarga y carga la plantilla desde Importar socios. Cuando existan filas que requieran revisión, aparecerán aquí."
      />
    );
  return (
    <div className="shell-page import-diagnostic-page reconciliation-page">
      <header className="page-heading reconciliation-heading">
        <div>
          <p className="kicker">M04.6 · CONTROL DE ALÍCUOTAS</p>
          <h1>Conciliación de titulares</h1>
          <p>Selecciona un cupo para revisar su información y registrar una decisión auditada.</p>
        </div>
      </header>
      <section className="diagnostic-summary reconciliation-metrics" aria-label="Resumen">
        <article>
          <span>Total</span>
          <strong>{board.rows.length}</strong>
        </article>
        <article>
          <span>Pendientes</span>
          <strong>{board.pending}</strong>
        </article>
        <article>
          <span>Resueltas</span>
          <strong>{board.resolved}</strong>
        </article>
      </section>

      <section className="reconciliation-toolbar">
        <div className="reconciliation-search">
          <Search aria-hidden="true" />
          <input
            aria-label="Buscar cupo o socio"
            placeholder="Buscar por cupo, nombre o cédula"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <nav className="preview-filters" aria-label="Estado">
          {(['pending', 'resolved'] as const).map((value) => (
            <button
              type="button"
              key={value}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {value === 'pending'
                ? `Por revisar (${board.pending})`
                : `Historial (${board.resolved})`}
            </button>
          ))}
        </nav>
      </section>
      <div className="reconciliation-workspace">
        <section className="reconciliation-roster" aria-label="Listado de alícuotas">
          <header>
            <div>
              <strong>Alícuotas</strong>
              <span>{rows.length} resultados</span>
            </div>
          </header>
          <div className="reconciliation-roster__scroll">
            {rows.map((row) => (
              <button
                type="button"
                key={row.id}
                className={`reconciliation-roster__item${selectedId === row.id ? ' is-selected' : ''}`}
                onClick={() => setSelectedId(row.id)}
                aria-current={selectedId === row.id ? 'true' : undefined}
              >
                <span className="reconciliation-roster__number">{row.slotNumber ?? '—'}</span>
                <span className="reconciliation-roster__identity">
                  <strong>{row.fullName ?? 'Cupo sin titular'}</strong>
                  <small>
                    {row.nationalId ? `Cédula ${row.nationalId}` : 'Sin cédula registrada'}
                  </small>
                </span>
                <span
                  className={`reconciliation-roster__status reconciliation-roster__status--${row.status}`}
                >
                  {row.status === 'resolved' ? <CheckCircle2 aria-hidden="true" /> : null}
                  {statusLabel(row.resolutionAction)}
                </span>
                <ChevronRight aria-hidden="true" className="reconciliation-roster__arrow" />
              </button>
            ))}
            {!rows.length ? (
              <div className="reconciliation-roster__empty">
                No existen conciliaciones pendientes.
              </div>
            ) : null}
          </div>
        </section>
        <section className="reconciliation-detail" aria-label="Detalle de la alícuota">
          {notice ? (
            <div className="ui-alert reconciliation-detail__notice" role="status">
              {notice}
            </div>
          ) : null}
          {selected ? (
            <DecisionPanel
              row={selected}
              reason={reasons[selected.id] ?? ''}
              nationalId={nationalIds[selected.id] ?? ''}
              selectedMember={selectedMembers[selected.id] ?? ''}
              busy={busy === selected.id}
              onReason={(value) => setReasons((current) => ({ ...current, [selected.id]: value }))}
              onNationalId={(value) =>
                setNationalIds((current) => ({ ...current, [selected.id]: value }))
              }
              onMember={(value) =>
                setSelectedMembers((current) => ({ ...current, [selected.id]: value }))
              }
              onResolve={(action, nationalId, memberId) =>
                resolve(selected, action, nationalId, memberId)
              }
            />
          ) : (
            <div className="reconciliation-detail__empty">
              <UsersRound aria-hidden="true" />
              <strong>Selecciona una alícuota</strong>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function DecisionPanel({
  row,
  reason,
  nationalId,
  selectedMember,
  busy,
  onReason,
  onNationalId,
  onMember,
  onResolve,
}: {
  row: MemberImportReconciliationRow;
  reason: string;
  nationalId: string;
  selectedMember: string;
  busy: boolean;
  onReason: (v: string) => void;
  onNationalId: (v: string) => void;
  onMember: (v: string) => void;
  onResolve: (a: MemberImportResolutionAction, n?: string, m?: string) => void;
}) {
  const reasonReady = reason.trim().length >= 10,
    canCreate = Boolean(
      row.fullName && !isVacant(row) && (row.nationalId || /^\d{6,20}$/.test(nationalId)),
    ),
    canAssign =
      Boolean(row.slotNumber) &&
      (row.status === 'pending' || isReleased(row)) &&
      row.resolutionAction !== 'assign-existing-member',
    needsDecision = row.status === 'pending' || canAssign;
  return (
    <>
      <header className="reconciliation-detail__header">
        <div>
          <span>ALÍCUOTA</span>
          <strong>{row.slotNumber ?? 'Sin número'}</strong>
        </div>
        <span
          className={`reconciliation-detail__badge reconciliation-detail__badge--${row.status}`}
        >
          {statusLabel(row.resolutionAction)}
        </span>
      </header>
      <div className="reconciliation-detail__identity">
        <span>Titular registrado</span>
        <strong>{row.fullName ?? 'Sin titular actual'}</strong>
        <small>
          {row.nationalId ? `Cédula ${row.nationalId}` : 'Documentación de identidad no entregada'}
        </small>
      </div>
      {row.status === 'pending' && row.issues.length ? (
        <div className="reconciliation-detail__issues">
          <strong>Revisión requerida</strong>
          <ul>
            {row.issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {needsDecision ? (
        <div className="reconciliation-detail__form">
          <label htmlFor={`reason-${row.id}`}>
            Motivo de la decisión <span>*</span>
          </label>
          <textarea
            id={`reason-${row.id}`}
            rows={4}
            maxLength={500}
            value={reason}
            onChange={(e) => onReason(e.target.value)}
            placeholder="Describe la evidencia y la decisión (mínimo 10 caracteres)"
          />
          {reason.length > 0 && !reasonReady ? (
            <small>Faltan {10 - reason.trim().length} caracteres.</small>
          ) : null}
        </div>
      ) : (
        <div className="reconciliation-detail__closed">
          <CheckCircle2 aria-hidden="true" />
          <div>
            <strong>Conciliación completada</strong>
            <span>Esta decisión conserva historial y auditoría.</span>
          </div>
        </div>
      )}
      {row.status === 'pending' && !row.nationalId && !isVacant(row) ? (
        <div className="reconciliation-detail__section">
          <strong>Identificación</strong>
          <label htmlFor={`national-${row.id}`}>Cédula verificada</label>
          <input
            id={`national-${row.id}`}
            inputMode="numeric"
            value={nationalId}
            onChange={(e) => onNationalId(e.target.value.replace(/\D/g, ''))}
          />
          <button
            type="button"
            className="ui-button ui-button--secondary"
            disabled={!reasonReady || busy}
            onClick={() => onResolve('release-undocumented-holder')}
          >
            Liberar cupo conservando historial
          </button>
        </div>
      ) : null}
      {row.status === 'pending' && canCreate ? (
        <div className="reconciliation-detail__actions">
          <button
            type="button"
            className="ui-button ui-button--primary"
            disabled={!reasonReady || busy}
            onClick={() => onResolve('create-active-member', row.nationalId ?? nationalId)}
          >
            Validar como socio activo
          </button>
          <button
            type="button"
            className="ui-button ui-button--secondary"
            disabled={!reasonReady || busy}
            onClick={() => onResolve('create-inactive-member', row.nationalId ?? nationalId)}
          >
            Registrar baja histórica
          </button>
        </div>
      ) : null}
      {row.status === 'pending' && isVacant(row) ? (
        <div className="reconciliation-detail__actions">
          <button
            type="button"
            className="ui-button ui-button--secondary"
            disabled={!reasonReady || busy}
            onClick={() => onResolve('confirm-vacant')}
          >
            Confirmar vacante
          </button>
          <button
            type="button"
            className="ui-button ui-button--secondary"
            disabled={!reasonReady || busy}
            onClick={() => onResolve('preserve-historical')}
          >
            Conservar referencia
          </button>
        </div>
      ) : null}
      {row.status === 'pending' && !isVacant(row) ? (
        <p className="reconciliation-detail__guidance">
          Si el titular renunció, fue liquidado, reemplazado o falleció, usa{' '}
          <strong>Registrar baja histórica</strong> e indica la causa y fecha real en el motivo.
          Quedará fuera del padrón activo y conservará su trazabilidad.
        </p>
      ) : null}
      {row.status === 'pending' && !isVacant(row) ? (
        <button
          type="button"
          className="ui-button ui-button--secondary reconciliation-detail__reference"
          disabled={!reasonReady || busy}
          onClick={() => onResolve('preserve-historical')}
        >
          Conservar solo referencia histórica
        </button>
      ) : null}
      {canAssign ? (
        <MemberPicker
          rowId={row.id}
          value={selectedMember}
          disabled={!reasonReady || busy}
          onChange={onMember}
          onAssign={() => onResolve('assign-existing-member', undefined, selectedMember)}
        />
      ) : null}
    </>
  );
}
